import localforage from 'localforage';
import { INotification } from '../services/customer-api-client';
import { areTheSameDates, datetimeToString } from '../utils';
import { IDefaultCurrencies, IPurchase, Theme } from '../contexts/LottoContext';
import { localVariableDriver } from './CustomLocalForageDriver';

const NOTIFICATIONS = 'previousNotifications';
const QUICKPICK_BOTTOM_BUTTONS = 'quickpickBottomButtons';
const SPLASH_SCREEN = 'alreadyShowedSplashScreen';
const SPLASH_SCREEN_DATE = 'splashScreenLastDate';
const PREVENT_LOGGING = 'preventLogging';
const SWIPE_ANIMATION = 'alreadyShowedSwipeAnimation';
const RECENT_SEARCHES = 'lastRecentSearch';
const CURRENT_SERVER_DATETIME = 'currentServerDatetime';
const STORED_PURCHASE_KEY = 'spk';
const CURRENCY_CODE = 'currencyCode';
const TOKEN = 'tk';
const MAIN_HOST = 'main_host';
const LOCALIZATION = 'localization';
const SKIN_KEY = 'sk';
const THEME_KEY = 'theme';
const SPLASH_SCREEN_LIMIT_KEY = 'splashScreenLimit';
const SPLASH_SCREEN_COUNTER_KEY = 'splashScreenCounter';
const RUSHIN_RABBIT_AMOUNT = 'rushinRabbitAmount';
const PICKIFY_AMOUNT = 'pickifyAmount';
const FORCE_IFRAME = 'forceIframe';
const SHOW_STAR_CAMPAIGN_ANIMATION = 'showStarCampaignAnimation';
const LOTTO_CURRENCIES = 'lottoDefaultCurrencies';
const TRIZ_DONT_SHOW_AGAIN = 'trizDontShowAgain';
const IDENTIFICATIONS = 'identifications';
const MATOMO_SCRIPT = 'mtm';

localforage.defineDriver(localVariableDriver);

localforage.setDriver([localforage.INDEXEDDB, 'localVariableStorage']);

export const previousNotifications = {
  async add(notifications: INotification[]) {
    const entries: INotification[] =
      (await localforage.getItem<INotification[]>(NOTIFICATIONS)) || [];
    for (let i = notifications.length - 1; i >= 0; i--) {
      entries.unshift(notifications[i]);
    }
    localforage.setItem(NOTIFICATIONS, entries);
  },
  async get() {
    const entries: INotification[] =
      (await localforage.getItem<INotification[]>(NOTIFICATIONS)) || [];
    return entries;
  },
  async markAllAsRead() {
    const entries = await this.get();
    entries.forEach((entry) => (entry.read = true));
    localforage.setItem(NOTIFICATIONS, entries);
  },
  async markAsRead(index: number) {
    const entries = await this.get();
    entries[index].read = true;
    localforage.setItem(NOTIFICATIONS, entries);
  },
};

export const quickPickButtons = {
  markAsShown() {
    localforage.setItem(QUICKPICK_BOTTOM_BUTTONS, true);
  },
  async get() {
    const value: boolean | null = await localforage.getItem<boolean>(QUICKPICK_BOTTOM_BUTTONS);
    return value === true;
  },
};

export const splashScreen = {
  async showed() {
    const splashScreenHasBeenShown = await localforage.getItem<boolean>(SPLASH_SCREEN);
    const date = await localforage.getItem<Date>(SPLASH_SCREEN_DATE);
    let isSameDate = false;
    if (date) {
      const now = new Date();
      isSameDate = areTheSameDates(date, now);
    }
    return splashScreenHasBeenShown === true && isSameDate;
  },
  show() {
    localforage.setItem(SPLASH_SCREEN, true);
    localforage.setItem(SPLASH_SCREEN_DATE, new Date());
  },
  setLimit(value: string) {
    localforage.setItem(SPLASH_SCREEN_LIMIT_KEY, value);
  },
  async getLimit(): Promise<number> {
    const value = (await localforage.getItem<string>(SPLASH_SCREEN_LIMIT_KEY)) || '0';
    return parseInt(value, 10);
  },
  async increaseCounter() {
    const counter = (await localforage.getItem<number>(SPLASH_SCREEN_COUNTER_KEY)) || 0;
    localforage.setItem(SPLASH_SCREEN_COUNTER_KEY, counter + 1);
  },
  async getCounter() {
    const counter = await localforage.getItem<number>(SPLASH_SCREEN_COUNTER_KEY);
    return counter || 0;
  },
};

export const preventLogging = {
  set(preventLogging: boolean) {
    localforage.setItem(PREVENT_LOGGING, preventLogging);
  },
  async get() {
    const value: boolean | null = await localforage.getItem<boolean>(PREVENT_LOGGING);
    return value === true;
  },
};

export const swipeAnimation = {
  async showed() {
    const value: boolean | null = await localforage.getItem<boolean>(SWIPE_ANIMATION);
    return value === true;
  },
  show() {
    localforage.setItem(SWIPE_ANIMATION, true);
  },
  hideAnimation() {
    localforage.setItem(SWIPE_ANIMATION, false);
  },
};

export const recentSearches = {
  set(date: string) {
    localforage.setItem(RECENT_SEARCHES, date);
  },
  async get() {
    const value: string | null = await localforage.getItem<string>(RECENT_SEARCHES);
    return value || '';
  },
};

export const currentServerDatetime = {
  set(datetime: string) {
    localforage.setItem(CURRENT_SERVER_DATETIME, datetime);
  },
  async get() {
    const value: string | null = await localforage.getItem<string>(CURRENT_SERVER_DATETIME);
    if (!!value) return value;
    return datetimeToString(new Date());
  },
};

export const storedPurchase = {
  set(purchase: IPurchase) {
    localforage.setItem(STORED_PURCHASE_KEY, purchase);
  },
  async get() {
    const value: IPurchase | null = await localforage.getItem<IPurchase>(STORED_PURCHASE_KEY);
    return value;
  },
};

export const currencyCodeSetting = {
  set(currency: string) {
    localforage.setItem(CURRENCY_CODE, currency);
  },
  async get() {
    const value: string | null = await localforage.getItem<string>(CURRENCY_CODE);
    return value || '';
  },
};

let tokenPromise: Promise<void>;

export const authorizationTokenStorage = {
  set(token: string) {
    tokenPromise = new Promise((resolve) => {
      localforage.setItem(TOKEN, token, resolve);
    });
  },
  async get(): Promise<string | null> {
    const value: string | null = await localforage.getItem<string>(TOKEN);
    if (!!value) {
      return value;
    }
    await tokenPromise;
    const value2 = await localforage.getItem<string>(TOKEN);
    return value2;
  },
};

export const mainHostStorage = {
  set(value: string) {
    localforage.setItem(MAIN_HOST, value);
  },
  async get(): Promise<string | null> {
    const value: string | null = await localforage.getItem<string>(MAIN_HOST);
    return value;
  },
};

export const localizationStorage = {
  set(value: string) {
    localforage.setItem(LOCALIZATION, value);
  },
  async get(): Promise<string | null> {
    const value: string | null = await localforage.getItem<string>(LOCALIZATION);
    return value;
  },
};

export enum Skin {
  Default = 'default',
  Cronos = 'cronos',
}

export const skinStorage = {
  set(value: Skin) {
    localforage.setItem(SKIN_KEY, value);
  },
  async get(): Promise<Skin> {
    const value: Skin = (await localforage.getItem<Skin>(SKIN_KEY)) || Skin.Default;
    return value;
  },
};

export const themeStorage = {
  set(value: Theme) {
    localforage.setItem(THEME_KEY, value);
  },
  async get(): Promise<Theme> {
    const value: Theme = (await localforage.getItem<Theme>(THEME_KEY)) || Theme.Default;
    return value;
  },
};

export const forceIframeStorage = {
  set(value: boolean) {
    localforage.setItem(FORCE_IFRAME, value);
  },
  async get(): Promise<boolean | null> {
    const value: boolean | null = await localforage.getItem<boolean>(FORCE_IFRAME);
    return value;
  },
};

export const showStarCampaignAnimation = {
  set(showAnimation: boolean) {
    localforage.setItem(SHOW_STAR_CAMPAIGN_ANIMATION, showAnimation);
  },
  async get() {
    const value: boolean | null = await localforage.getItem<boolean>(SHOW_STAR_CAMPAIGN_ANIMATION);
    return value === true;
  },
};

export const rushinRabbitAmount = {
  set(amount: number) {
    localforage.setItem(RUSHIN_RABBIT_AMOUNT, amount);
  },
  async get() {
    const value: number | null = await localforage.getItem<number>(RUSHIN_RABBIT_AMOUNT);
    return value || 0;
  },
};

export const pickifyAmount = {
  set(amount: number) {
    localforage.setItem(PICKIFY_AMOUNT, amount);
  },
  async get() {
    const value: number | null = await localforage.getItem<number>(PICKIFY_AMOUNT);
    return value || 0;
  },
};

export const lottoDefaultCurrencies = {
  set(amount: IDefaultCurrencies) {
    localforage.setItem(LOTTO_CURRENCIES, amount);
  },
  async get() {
    const value: IDefaultCurrencies | null = await localforage.getItem<IDefaultCurrencies>(
      LOTTO_CURRENCIES
    );
    return value;
  },
  async remove() {
    await localforage.removeItem(LOTTO_CURRENCIES);
  },
};

export const trizStorage = {
  setDontShowAgain(value: boolean) {
    localforage.setItem(TRIZ_DONT_SHOW_AGAIN, value);
  },
  async getDontShowAgain() {
    const value: boolean | null = await localforage.getItem<boolean>(TRIZ_DONT_SHOW_AGAIN);
    return value === true;
  },
};

interface IIdentifications {
  playerId: string;
  affiliateId: string;
}

export const identificationsStorage = {
  set(value: IIdentifications) {
    localforage.setItem(IDENTIFICATIONS, value);
  },
  async get(): Promise<IIdentifications | null> {
    const value: IIdentifications | null = await localforage.getItem<IIdentifications>(
      IDENTIFICATIONS
    );
    return value;
  },
};

export const matomoScriptStorage = {
  set(value: string) {
    localforage.setItem(MATOMO_SCRIPT, value);
  },
  async get(): Promise<string | null> {
    const value: string | null = await localforage.getItem<string>(MATOMO_SCRIPT);
    return value;
  },
};
